import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardGrid, type GridWidget } from "@/components/dashboard-grid/DashboardGrid";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import type { useBoard } from "@/components/dashboard-grid/useBoard";
import { useDashboardOverview } from "@/hooks/useDashboardOverview";
import { useDashboardOrigination } from "@/hooks/useDashboardOrigination";
import { useFinancialsOverview } from "@/hooks/useFinancials";
import { PipelineIceberg } from "@/components/financials/PipelineIceberg";
import { BUYER_FUNNEL_PREFIX, buyerFunnelId } from "@shared/dashboardLayout";
import { useLocation } from "wouter";
import { useBdTracker } from "@/hooks/useBdTracker";
import { BdScoreboard } from "@/components/coi/bd/BdScoreboard";
import { BdReferrers } from "@/components/coi/bd/BdReferrers";
import { bdHref } from "@/components/coi/bd/bdParams";
import { KpiTiles } from "./widgets/KpiTiles";
import { ProspectsInPlay } from "./widgets/ProspectsInPlay";
import { WhereItComesFrom } from "./widgets/WhereItComesFrom";
import { ProspectFunnel } from "./widgets/ProspectFunnel";
import { ProspectTouches } from "./widgets/ProspectTouches";
import { ActiveDealsWidget } from "./widgets/ActiveDealsWidget";
import { TodayWidget } from "./widgets/TodayWidget";
import { BuyerFunnelWidget } from "./widgets/BuyerFunnelWidget";

const Loading = () => <Skeleton className="h-full w-full rounded-lg" />;

function OpsIceberg() {
  const { data } = useFinancialsOverview(new Date().getFullYear());
  return (
    <WidgetPanel title="Pipeline" meta="starred prospects over active deals" bodyClassName="px-2">
      {data ? <PipelineIceberg data={data} includeUpside /> : <Loading />}
    </WidgetPanel>
  );
}

/** Every widget this viewer may place on Operations, keyed by layout id. */
export function useOpsWidgets(canViewFinancials: boolean, meName: string | null, layoutIds: string[]): Record<string, GridWidget> {
  const ov = useDashboardOverview().data;
  const o = useDashboardOrigination().data;
  // Fetched only when one of the BD widgets is on the board.
  const wantsBd = layoutIds.includes("bdScoreboard") || layoutIds.includes("referrers");
  const bd = useBdTracker("month", "all", { enabled: wantsBd }).data;
  const [, navigate] = useLocation();
  const w = (id: string, title: string, description: string, render: () => ReactNode): [string, GridWidget] => [id, { id, title, description, render }];

  const widgets: Record<string, GridWidget> = Object.fromEntries([
    w("tiles", "KPI tiles", "The eight origination numbers across the top", () => (o ? <KpiTiles o={o} deals={ov?.activeDeals} canStar={canViewFinancials} /> : <Loading />)),
    w("prospects", "Prospects in play", "MVA open, MVA delivered, in conversation", () => (o ? <ProspectsInPlay rows={o.prospectsInPlay} canStar={canViewFinancials} meName={meName} /> : <Loading />)),
    w("sources", "Where it comes from", "Prospects and mandates by source", () => (o ? <WhereItComesFrom o={o} /> : <Loading />)),
    w("deals", "Active deals", "Stage, milestones and buyer counts per deal", () => (ov ? <ActiveDealsWidget deals={ov.activeDeals} /> : <Loading />)),
    w("pfunnel", "Prospect funnel", "Prospects by stage", () => (o ? <ProspectFunnel o={o} /> : <Loading />)),
    w("touches", "Prospect touches", "Emails, meetings and calls per week", () => (o ? <ProspectTouches o={o} /> : <Loading />)),
    w("today", "Today", "Deadlines, inbound, tasks, reminders, intro suggestions", () => (
      ov ? <TodayWidget attention={ov.needsAttention} deals={ov.activeDeals} inbound={o?.inbound} /> : <Loading />
    )),
    ...(canViewFinancials ? [w("iceberg", "Pipeline iceberg", "Fees by company, starred prospects over active deals", () => <OpsIceberg />)] : []),
    w("bdScoreboard", "BD scoreboard", "Touchpoints, meetings, COIs added and prospects sourced per person, this month", () => (
      bd ? <BdScoreboard data={bd} title="BD this month" onSelect={(id) => navigate(bdHref("month", id))} /> : <Loading />
    )),
    w("referrers", "Who's sending us business", "Referrers this year and what came of each introduction", () => (bd ? <BdReferrers data={bd} /> : <Loading />)),
    ...(ov?.funnels ?? []).map((f) =>
      w(buyerFunnelId(f.client.id), `Buyer funnel · ${f.projectName ?? f.client.name}`, "Teaser to LOI for one deal", () => <BuyerFunnelWidget funnel={f} />)),
  ]);
  // A saved funnel for a deal that has since closed still renders, so it can be removed.
  for (const id of layoutIds) {
    if (id.startsWith(BUYER_FUNNEL_PREFIX) && !widgets[id]) {
      widgets[id] = { id, title: "Buyer funnel", description: "Deal no longer active", render: () => <BuyerFunnelWidget funnel={undefined} /> };
    }
  }
  return widgets;
}

export function OperationsBoard({ board, widgets }: { board: ReturnType<typeof useBoard>; widgets: Record<string, GridWidget> }) {
  return <DashboardGrid key={board.gridKey} board="ops" layout={board.layout} widgets={widgets} editing={board.editing} onChange={board.change} />;
}
